import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppDispatch } from '../../redux/hooks'
import { clearSecureState } from '../SecureRoute/slice'
import {
  DropdownWrapper,
  DropdownButton,
  DropdownContent,
  MenuItem,
  MenuIcon,
  MenuLabel,
  Separator
} from './NavigationMenu.styled'

interface NavigationMenuProps {
  isSuperAdmin: boolean
  isEaterAdmin: boolean
  isRestaurantAdmin: boolean
  currentTitle?: string
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  isSuperAdmin,
  isEaterAdmin,
  isRestaurantAdmin,
  currentTitle
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const { logout } = useAuth0()
  const dispatch = useAppDispatch()

  const handleLogout = (): void => {
    // Clear Redux secure state and localStorage
    dispatch(clearSecureState());
    // Auth0 logout will handle redirecting to login
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }

  const handleNavigate = (path: string): void => {
    navigate(path)
    setIsOpen(false)
  }

  const isActive = (title: string): boolean => currentTitle === title

  return (
    <DropdownWrapper>
      <DropdownButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
        <MenuLabel>Menu</MenuLabel>
        <span style={{ marginLeft: '8px' }}>▼</span>
      </DropdownButton>

      {isOpen && (
        <DropdownContent>
          {isSuperAdmin && (
            <>
              <MenuItem
                isActive={isActive('Locations')}
                onClick={() => handleNavigate('/locations')}
              >
                <MenuIcon>📍</MenuIcon>
                <span>Locations</span>
              </MenuItem>
              <Separator />
            </>
          )}

          {isEaterAdmin && (
            <MenuItem
              isActive={isActive('Eaters')}
              onClick={() => handleNavigate('/eaters')}
            >
              <MenuIcon>👥</MenuIcon>
              <span>Eaters</span>
            </MenuItem>
          )}

          {isSuperAdmin && (
            <>
              <MenuItem
                isActive={isActive('Restaurant Approvals')}
                onClick={() => handleNavigate('/admin/restaurants/approvals')}
              >
                <MenuIcon>✅</MenuIcon>
                <span>Restaurant Approvals</span>
              </MenuItem>
              <MenuItem
                isActive={isActive('Menu Load Requests')}
                onClick={() => handleNavigate('/menu-load-request')}
              >
                <MenuIcon>📋</MenuIcon>
                <span>Menu Load Requests</span>
              </MenuItem>
              <MenuItem
                isActive={isActive('Missing Menu Queue')}
                onClick={() => handleNavigate('/missing-menu-queue')}
              >
                <MenuIcon>🔍</MenuIcon>
                <span>Missing Menu Queue</span>
              </MenuItem>
              <MenuItem
                isActive={isActive('Food Areas')}
                onClick={() => handleNavigate('/food-areas')}
              >
                <MenuIcon>🗺️</MenuIcon>
                <span>Food Areas</span>
              </MenuItem>
              <Separator />
              <MenuItem
                isActive={isActive('Unblock Users')}
                onClick={() => handleNavigate('/unblock-users')}
              >
                <MenuIcon>🔓</MenuIcon>
                <span>Unblock Users</span>
              </MenuItem>
              <MenuItem
                isActive={isActive('Admin Access Requests')}
                onClick={() => handleNavigate('/access-control')}
              >
                <MenuIcon>🔐</MenuIcon>
                <span>Admin Access</span>
              </MenuItem>
              <MenuItem
                isActive={isActive('Admins')}
                onClick={() => handleNavigate('/admins')}
              >
                <MenuIcon>👨‍💼</MenuIcon>
                <span>Admins</span>
              </MenuItem>
              <Separator />
            </>
          )}

          <MenuItem
            isActive={false}
            onClick={handleLogout}
          >
            <MenuIcon>🚪</MenuIcon>
            <span>Logout</span>
          </MenuItem>
        </DropdownContent>
      )}
    </DropdownWrapper>
  )
}

export default NavigationMenu
